import { isClinicIndustry, isOrderFirstShop, isRetailIndustry } from '@/lib/industry';
import { hasModule } from '@/lib/modules';
import { can } from '@/lib/permissions';

/** Where `/` sends each role (docs/04 §4). First candidate that exists as a route wins; otherwise the home placeholder shows. */
export function homeCandidates(roles: readonly string[], permissions: readonly string[]): string[] {
  const has = (r: string) => roles.includes(r);
  if (has('owner')) return ['/kpi', '/today'];
  if (has('branch_manager')) return ['/today'];
  if (has('front_desk')) return ['/today'];
  if (has('practitioner') || has('staff')) return ['/me/today'];
  // Custom roles: decide by what the member can do.
  if (permissions.some((p) => p.startsWith('appointments:') && !p.endsWith(':own'))) return ['/today'];
  return ['/me/today'];
}

/** Open redirect guard: only same-origin absolute paths. */
export function safeRedirect(value: unknown): string | undefined {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') ? value : undefined;
}

/** `search` for a redirect to /sign-in: remember where the user was, except for the default landing page. */
export function signInSearch(href: string): { redirect?: string } {
  const target = safeRedirect(href);
  return target && target !== '/' ? { redirect: target } : {};
}

/**
 * Where owners / managers of a clinic land: the dashboard. Everyone else keeps the role home.
 * Needs the money permission and the checkout module, otherwise the dashboard would be empty or denied.
 */
export function dashboardLanding(m: {
  tenant: { industry: string; rental?: unknown };
  permissions: readonly string[];
  modules: readonly string[];
}): string | undefined {
  // Rental shops open on the pick-up / return board
  if (m.tenant.rental && can(m.permissions, 'appointments:read')) return '/rentals';
  // Cafés that take orders open on the order screen (bookings are the side job there)
  if (isOrderFirstShop(m) && can(m.permissions, 'table_orders:read')) return '/orders';
  // Lite (booking-only) shops have no dashboard: everyone lands on Today
  if (hasModule(m.modules, 'lite')) return '/today';
  // Shops that only sell goods have no calendar: owners / managers land on the counter sale
  if (
    isRetailIndustry(m.tenant.industry) &&
    hasModule(m.modules, 'inventory') &&
    can(m.permissions, 'bills:write')
  ) {
    return '/inventory/sell';
  }
  return isClinicIndustry(m.tenant.industry) &&
    can(m.permissions, 'kpi:read') &&
    hasModule(m.modules, 'checkout')
    ? '/sales-summary'
    : undefined;
}
